import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  LeftCircleOutlined,
  PrinterOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { AButton, ATitle, Loading } from '../../../../components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CrcReport } from '../../../../shared/reports';
import { useToast } from '../../../../hooks/toast';
import { exportCrcReport } from '../../../../http/crc';
import { base64ToString } from '../../../../shared/utils/pdf-utils';
import { ContainerPrint, HeaderContainer } from './styles';

interface ICachedReportData {
  html: string;
  periodFrom: string | null;
  periodTo: string | null;
  reportType: string | null;
  filename: string | null;
}

const CrcReportsPrint: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { addToast } = useToast();
  const [loading, setLoading] = useState<boolean>(true);
  const [reportData, setReportData] = useState<ICachedReportData>({
    html: '',
    periodFrom: null,
    periodTo: null,
    reportType: null,
    filename: null,
  });

  const report_type = searchParams.get('report_type') || 'overview';
  const format = (searchParams.get('format') as 'pdf' | 'xlsx' | 'csv' | null) || 'pdf';
  const period_from = searchParams.get('period_from') || undefined;
  const period_to = searchParams.get('period_to') || undefined;
  const atendente_ids_raw = searchParams.get('atendente_ids');
  const professional_ids_raw = searchParams.get('professional_ids');
  const especialidade = searchParams.get('especialidade') || undefined;

  const atendente_ids = useMemo(() => {
    if (!atendente_ids_raw) return undefined;
    try {
      return atendente_ids_raw.split(',').filter(Boolean);
    } catch {
      return undefined;
    }
  }, [atendente_ids_raw]);

  const professional_ids = useMemo(() => {
    if (!professional_ids_raw) return undefined;
    try {
      return professional_ids_raw.split(',').filter(Boolean);
    } catch {
      return undefined;
    }
  }, [professional_ids_raw]);

  const loadAndGenerateReport = useCallback(async () => {
    try {
      setLoading(true);
      const res = await exportCrcReport({
        report_type: report_type as any,
        format: 'pdf',
        period_from,
        period_to,
        atendente_ids,
        professional_ids,
        especialidade,
      });
      let html = '';
      if (res && typeof res.content_base64 === 'string' && res.content_base64.length > 0) {
        html = base64ToString(res.content_base64);
      }
      setReportData({
        html,
        periodFrom: period_from || null,
        periodTo: period_to || null,
        reportType: report_type || null,
        filename: res?.filename || null,
      });
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Não foi possível gerar o relatório para impressão.';
      addToast({
        type: 'error',
        title: 'Erro ao carregar relatório',
        description: msg,
      });
    } finally {
      setLoading(false);
    }
  }, [
    report_type,
    period_from,
    period_to,
    atendente_ids,
    professional_ids,
    especialidade,
    addToast,
  ]);

  useEffect(() => {
    loadAndGenerateReport();
  }, [loadAndGenerateReport]);

  const handlePrint = () => {
    if (typeof window !== 'undefined') {
      window.print();
    }
  };

  const handleDownload = async (targetFormat: 'pdf' | 'xlsx' | 'csv') => {
    try {
      setLoading(true);
      await exportCrcReport({
        report_type: report_type as any,
        format: targetFormat,
        period_from,
        period_to,
        atendente_ids,
        professional_ids,
        especialidade,
      });
      addToast({
        type: 'success',
        title: 'Download iniciado',
        description:
          targetFormat === 'pdf'
            ? 'Relatório PDF sendo baixado.'
            : 'Relatório CSV/Planilha sendo baixado.',
      });
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Não foi possível baixar o relatório.';
      addToast({
        type: 'error',
        title: 'Erro ao baixar',
        description: msg,
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loading load />;

  return (
    <>
      <HeaderContainer>
        <ATitle icon={<PrinterOutlined />}>Imprimir Relatório CRC</ATitle>
        <ContainerPrint>
          <AButton
            type="default"
            onClick={() => navigate('/crc/reports')}
            icon={<LeftCircleOutlined />}
          >
            Voltar
          </AButton>
          <AButton
            type="default"
            ghost
            onClick={() => handleDownload('csv')}
            icon={<DownloadOutlined />}
          >
            Baixar CSV
          </AButton>
          <AButton
            type="default"
            ghost
            onClick={() => handleDownload('xlsx')}
            icon={<DownloadOutlined />}
          >
            Baixar Excel
          </AButton>
          <AButton
            type="primary"
            ghost
            onClick={() => handleDownload('pdf')}
            icon={<DownloadOutlined />}
          >
            Baixar PDF
          </AButton>
          <AButton
            type="primary"
            onClick={handlePrint}
            icon={<PrinterOutlined />}
          >
            Imprimir
          </AButton>
        </ContainerPrint>
      </HeaderContainer>
      <CrcReport
        htmlContent={reportData.html}
        periodFrom={reportData.periodFrom}
        periodTo={reportData.periodTo}
        reportType={reportData.reportType}
      />
    </>
  );
};

export default CrcReportsPrint;
